import { LOG_LEVEL } from '../../common/constants';
import { isBoolean, isEmpty, isUndefined } from '../type-check';
import loggerManager from './logger-manager';

/**
 * 向指定 userId 进行日志上报
 * 适用于 Client, SignalChannel, Connection, RemoteStream 等有明确上报 userId 的模块进行上报。
 *
 * 使用 loggerManager 的接口上报，共用日志队列。
 * @export
 * @class Logger
 */
export default class Logger {
  private _parentPath?: string;
  userId?: string;
  remoteUserId?: string;
  id: string;
  sdkAppId?: number;
  type?: string;
  isLocal?: boolean;
  constructor(options: { id: string, userId?: string, remoteUserId?: string; sdkAppId?: number, type?: 'main' | 'auxiliary', isLocal?: boolean; }) {
    this.id = options.id;
    this.userId = options.userId;
    this.sdkAppId = options.sdkAppId;
    this.remoteUserId = options.remoteUserId;
    this.isLocal = isBoolean(options.isLocal) ? options.isLocal : true;
    this.type = this.isLocal ? '' : options.type; // main or auxiliary
  }
  private getFullId() {
    if (this._parentPath && this.id) {
      return `${this._parentPath}-${this.id}`;
    }
    if (this._parentPath) {
      return this._parentPath;
    }
    return this.id;
  }
  createChild(options: { id: string, userId?: string, sdkAppId?: number, type?: 'main' | 'auxiliary', isLocal?: boolean; remoteUserId?: string; }) {
    const child = new Logger({
      id: options.id,
      userId: isUndefined(options.userId) ? this.userId : options.userId,
      sdkAppId: isUndefined(options.sdkAppId) ? this.sdkAppId : options.sdkAppId,
      type: isUndefined(options.type) ? (this.type as 'main' | 'auxiliary' | undefined) : options.type,
      isLocal: isUndefined(options.isLocal) ? this.isLocal : options.isLocal,
      remoteUserId: isUndefined(options.remoteUserId) ? this.remoteUserId : options.remoteUserId
    });
    child.bindParent(this);
    return child;
  }
  bindParent(parent: Logger) {
    const parentFullId = parent.getFullId();
    if (this._parentPath === parentFullId) return;
    this._parentPath = parentFullId;
    this.userId = parent.userId || this.userId;
    this.sdkAppId = parent.sdkAppId || this.sdkAppId;
    this.debug(`bindParent: ${parent.id}`);
  }
  setUserId(userId: string) {
    this.userId = userId;
  }

  setSdkAppId(sdkAppId: number) {
    this.sdkAppId = sdkAppId;
  }

  log(level: LOG_LEVEL, log: any[]) {
    // ↑ 上行
    // ↓ 下行
    // * 辅流 -> 下行辅流 ↓*
    const userId = this.isLocal ? this.userId : this.remoteUserId;
    const fullId = this.getFullId();
    log.unshift(`[${this.isLocal ? '↑' : '↓'}${this.type && this.type !== 'main' ? '*' : ''}${fullId}${userId ? `|${userId}` : ''}]`);
    // eslint-disable-next-line no-undef
    loggerManager.log(
      level,
      log,
      isUndefined(this.userId) || isEmpty(this.userId), // 没有配置 client userId，则向所有的 clients 上传日志
      this.userId,
      this.sdkAppId
    );
  }

  info(...args: any[]) {
    this.log(LOG_LEVEL.INFO, args);
  }

  debug(...args: any[]) {
    this.log(LOG_LEVEL.DEBUG, args);
  }

  warn(...args: any[]) {
    this.log(LOG_LEVEL.WARN, args);
  }

  error(...args: any[]) {
    this.log(LOG_LEVEL.ERROR, args);
  }
}
